<template>
  <div
    :class="{
      [$style['container']]: true,
      [$style['load-all-platforms']]: loadAllPlatforms,
    }"
  >
    <div>
      <ConnectAccountConnectorLayout
        :class="layoutClasses"
        :hide-tiles="!morePlatformsLoaded"
      >
        <template v-for="tile in tiles">
          <component
            :is="tile.component"
            v-if="tile.display"
            v-bind="tile.props"
            v-on="tile.events"
          ></component>
        </template>
      </ConnectAccountConnectorLayout>
      <DSButton
        v-if="!loadAllPlatforms || isMobile"
        :label="
          morePlatformsLoaded
            ? $t('common.buttons.seeLess')
            : $t('common.buttons.seeMore')
        "
        class="p-button-link"
        :class="$style['list-toggle']"
        @click="handleSeeMoreClick"
      />

      <ConnectPaymentWooCommerce ref="wooCommercePaymentConnector" />

      <slot name="cta" />
      <OauthConnect
        ref="oauthConnect"
        :provider-config="OAUTH_PROVIDER_CONFIG"
        @redirect="handleRedirect"
        @code="oauthSuccess"
        @connectPayment="openPaymentPopup"
        @error="handleOauthError"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mapRequestStatuses } from '@/utils/vuex-api-utils';
import { OAUTH_PROVIDER_CONFIG } from '@/data/oauth-provider-config';
import { CONNECTION_STATUS } from '@/data/connection-types';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import { trackLinkedin } from '@/utils/conversion-tracking';
import { mqlDefinition } from '@/utils/gtm';
import { LocalStorage, LocalStorageKeys } from '@/utils/local-storage';
import { useMobileDetection } from '@/composables/responsive';
import { clickTrackingMixin, modalsRedirectMixin } from '@/utils/vue-mixins';
import { SALES_PLATFORMS } from '@/data/platforms';
import { INTEGRATION_TYPES } from '@/data/onboarding';
import { segmentErrorTracking } from '@/utils/analytics-error-handling';
import externalAccountsComposables from '@/composables/external-accounts';
import { useContextRoot } from '@/utils/context-root';

import DSButton from 'primevue/button';
import OauthConnect from '@/components/OauthConnect.vue';
import ConnectAccountConnectorLayout from '@/pages/connected-accounts/components/ConnectAccountConnectorLayout.vue';
import ConnectedAccountOnboardingModal from '@/pages/connected-accounts/components/ConnectedAccountOnboardingModal.vue';
import ConnectWoocommerce from '@/pages/connected-accounts/components/connect/ConnectWoocommerce.vue';
import ConnectAmazonspapiVendor from '@/pages/connected-accounts/components/connect/ConnectAmazonspapiVendor.vue';
import ConnectShopify from '@/pages/connected-accounts/components/connect/ConnectShopify.vue';
import ConnectShopifyFivetran from '@/pages/connected-accounts/components/connect/ConnectShopifyFivetran.vue';
import ConnectAmazonspapi from '@/pages/connected-accounts/components/connect/ConnectAmazonspapi.vue';
import ConnectPaymentWooCommerce from '@/pages/connected-accounts/components/connect/ConnectPaymentWooCommerce.vue';

import amazonIcon from '@/assets/logos/amazon.png';
import amazonVendorCentralIcon from '@/assets/logos/amazon_vendor_central.png';
import stripeIcon from '@/assets/logos/stripe.png';
import squareIcon from '@/assets/logos/square.png';
import authorizenetIcon from '@/assets/logos/authorizenet.png';
import paypalIcon from '@/assets/logos/paypal.png';
import braintreeIcon from '@/assets/logos/braintree.png';
import bigcommerceIcon from '@/assets/logos/bigcommerce.png';

export default {
  components: {
    OauthConnect,
    ConnectPaymentWooCommerce,
    DSButton,
    ConnectAccountConnectorLayout,
  },
  mixins: [clickTrackingMixin, modalsRedirectMixin],
  props: {
    loadAllPlatforms: {
      type: Boolean,
      default: false,
    },
    // eslint-disable-next-line vue/no-unused-properties
    showError: { type: Boolean, default: false },
    // eslint-disable-next-line vue/no-unused-properties
    errorMessage: { type: String, required: false },
    eventTrackingFields: { type: Object, default: () => {} },
    showOnlyPaymentsCapacityConnections: { type: Boolean },
  },
  setup(props, context) {
    const {
      errors,
      showNextStepsError,
      updateTileToErrorStatus,
      handleTileClick,
      handleOauthError,
      trackNewPlatformModal,
      trackModal,
      trackRedirect,
      getExtAccStatus,
      hasPendingConnection,
      isFullyConnected,
      hasProcessingConnection,
      hasWarningConnection,
      trackSeeMoreClick,
    } = externalAccountsComposables(useContextRoot());
    const { isMobile } = useMobileDetection();

    return {
      isMobile,
      errors,
      showNextStepsError,
      updateTileToErrorStatus,
      handleTileClick,
      handleOauthError,
      trackNewPlatformModal,
      trackModal,
      trackRedirect,
      getExtAccStatus,
      hasPendingConnection,
      isFullyConnected,
      hasProcessingConnection,
      hasWarningConnection,
      trackSeeMoreClick,
    };
  },
  data() {
    return {
      platformType: 'sales',
      morePlatformsLoaded: false,
      paymentsCapacityConnections: this.showOnlyPaymentsCapacityConnections,
    };
  },
  computed: {
    ...mapGetters([
      'externalAccounts',
      'newSignupParams',
      'business',
      'productSegmentLabel',
      'oauthPayload',
      'showShopifyFivetranTile',
    ]),
    OAUTH_PROVIDER_CONFIG: () => OAUTH_PROVIDER_CONFIG,
    amazonLogo: () => amazonIcon,
    amazonVendorLogo: () => amazonVendorCentralIcon,
    stripeLogo: () => stripeIcon,
    squareLogo: () => squareIcon,
    authorizeNetLogo: () => authorizenetIcon,
    paypalLogo: () => paypalIcon,
    braintreeLogo: () => braintreeIcon,
    bigcommerceLogo: () => bigcommerceIcon,
    ...mapRequestStatuses({
      oauthConnectionRequest: 'OAUTH_CONNECT',
      shopifyFivetranConfigureRequest: 'CONNECTED_ACCOUNT_CONFIGURE',
    }),
    tiles() {
      const tiles = [
        {
          key: 'paypal',
          component: ConnectedAccountOnboardingModal,
          display: true,
          modalType: SALES_PLATFORMS.PAYPAL,
          events: {
            click: () => this.openModal(SALES_PLATFORMS.PAYPAL),
          },
          props: {
            title: 'paypal',
            logo: this.paypalLogo,
            success: this.isFullyConnected(SALES_PLATFORMS.PAYPAL),
            disconnected: this.hasPendingConnection(SALES_PLATFORMS.PAYPAL),
            error: this.errors.paypal,
            class: this.$style.connector,
            'logo-alt': this.$t('components.tileGroupConnectSales.payPalLogo'),
          },
        },
        {
          key: 'shopify',
          component: ConnectShopify,
          display: !this.showShopifyFivetranTile,
          events: {
            error: () => this.updateTileToErrorStatus(SALES_PLATFORMS.SHOPIFY),
            submit: (config) =>
              this.$refs.oauthConnect.launch('shopify', config),
            modalAction: this.handleModalAction,
          },
          props: {
            ref: 'shopify',
            'authorize-uri': OAUTH_PROVIDER_CONFIG.shopify.authorizeUri,
            success: this.isFullyConnected(SALES_PLATFORMS.SHOPIFY),
            disconnected: this.hasPendingConnection(SALES_PLATFORMS.SHOPIFY),
            warning: this.hasWarningConnection(SALES_PLATFORMS.SHOPIFY),
            error: this.errors.shopify,
            class: this.$style.connector,
          },
        },
        {
          key: 'shopify_fivetran',
          component: ConnectShopifyFivetran,
          display: this.showShopifyFivetranTile,
          events: {
            error: (errorMsg) => this.handleShopifyFivetranError(errorMsg),
            submit: (config) =>
              this.$refs.oauthConnect.launch('shopify_fivetran', config),
            modalAction: this.handleModalAction,
          },
          props: {
            ref: 'shopify_fivetran',
            'authorize-uri':
              OAUTH_PROVIDER_CONFIG.shopify_fivetran.authorizeUri,
            success: this.isFullyConnected(SALES_PLATFORMS.SHOPIFY),
            disconnected: this.hasPendingConnection(SALES_PLATFORMS.SHOPIFY),
            warning: this.hasWarningConnection(SALES_PLATFORMS.SHOPIFY),
            processing: this.hasProcessingConnection(SALES_PLATFORMS.SHOPIFY),
            error: this.errors.shopify_fivetran,
            class: this.$style.connector,
          },
        },
        {
          key: 'stripe',
          component: ConnectedAccountOnboardingModal,
          display: true,
          events: {
            click: () => this.openModal(SALES_PLATFORMS.STRIPE),
          },
          props: {
            title: 'stripe',
            logo: this.stripeLogo,
            'small-logo': true,
            success: this.isFullyConnected(SALES_PLATFORMS.STRIPE),
            disconnected: this.hasPendingConnection(SALES_PLATFORMS.STRIPE),
            error: this.errors.stripe,
            class: this.$style.connector,
            'logo-alt': this.$t('components.tileGroupConnectSales.stripLogo'),
          },
        },
        {
          key: 'square',
          component: ConnectedAccountOnboardingModal,
          display: true,
          events: {
            click: () => this.openModal(SALES_PLATFORMS.SQUARE),
          },
          props: {
            title: 'square',
            logo: this.squareLogo,
            success: this.isFullyConnected(SALES_PLATFORMS.SQUARE),
            disconnected: this.hasPendingConnection(SALES_PLATFORMS.SQUARE),
            error: this.errors.square,
            class: this.classes,
            'logo-alt': 'Square logo',
          },
        },
        {
          key: 'amazon',
          component: ConnectAmazonspapi,
          display: true,
          events: {
            error: () => this.updateTileToErrorStatus(SALES_PLATFORMS.AMAZON),
            success: this.trackAccountConnected,
            oauthError: this.handleOauthError,
            modalAction: this.handleModalAction,
            submit: (config) =>
              this.$refs.oauthConnect.launch('amazon', config),
          },
          props: {
            ref: 'amazon',
            title: 'Amazon',
            logo: this.amazonLogo,
            success: this.isFullyConnected(SALES_PLATFORMS.AMAZON),
            disconnected: this.hasPendingConnection(SALES_PLATFORMS.AMAZON),
            errors: this.errors,
            class: this.$style.connector,
            'corp-country': this.business.corpCountry,
          },
        },
        {
          key: 'amazonVendor',
          component: ConnectAmazonspapiVendor,
          display: true,
          events: {
            error: () =>
              this.updateTileToErrorStatus(SALES_PLATFORMS.AMAZONVENDOR),
            success: this.trackAccountConnected,
            oauthError: this.handleOauthError,
            modalAction: this.handleModalAction,
            submit: (config) =>
              this.$refs.oauthConnect.launch('amzvendor', config),
          },
          props: {
            ref: 'amazon',
            title: 'Amazon Vendor',
            logo: this.amazonVendorLogo,
            success: this.isFullyConnected(SALES_PLATFORMS.AMAZONVENDOR),
            disconnected: this.hasPendingConnection(
              SALES_PLATFORMS.AMAZONVENDOR,
            ),
            errors: this.errors,
            class: this.$style.connector,
            'corp-country': this.business.corpCountry,
          },
        },
        {
          key: 'authorizenet',
          component: ConnectedAccountOnboardingModal,
          display:
            this.showPaymentCapacityConnections ||
            this.isSuccessOrDisconnected(SALES_PLATFORMS.AUTHORIZENET),
          events: {
            click: () => this.openModal(SALES_PLATFORMS.AUTHORIZENET),
          },
          props: {
            title: 'authorizenet',
            logo: this.authorizeNetLogo,
            success: this.isFullyConnected(SALES_PLATFORMS.AUTHORIZENET),
            disconnected: this.hasPendingConnection(
              SALES_PLATFORMS.AUTHORIZENET,
            ),
            error: this.errors.authorizenet,
            class: this.classes,
            'logo-alt': 'AuthorizeNet logo',
          },
        },
        {
          key: 'bigcommerce',
          component: ConnectedAccountOnboardingModal,
          display:
            this.showPaymentCapacityConnections ||
            this.isSuccessOrDisconnected(SALES_PLATFORMS.BIGCOMMERCE),
          events: {
            click: () => this.openModal(SALES_PLATFORMS.BIGCOMMERCE),
          },
          props: {
            title: 'bigcommerce',
            logo: this.bigcommerceLogo,
            success: this.isFullyConnected(SALES_PLATFORMS.BIGCOMMERCE),
            disconnected: this.hasPendingConnection(
              SALES_PLATFORMS.BIGCOMMERCE,
            ),
            error: this.errors.bigcommerce,
            class: this.classes,
            'logo-alt': 'BigCommerce logo',
          },
        },
        {
          key: 'braintree',
          component: ConnectedAccountOnboardingModal,
          display:
            this.showPaymentCapacityConnections ||
            this.isSuccessOrDisconnected(SALES_PLATFORMS.BRAINTREE),
          events: {
            click: () => this.openModal(SALES_PLATFORMS.BRAINTREE),
          },
          props: {
            title: 'braintree',
            logo: this.braintreeLogo,
            success: this.isFullyConnected(SALES_PLATFORMS.BRAINTREE),
            disconnected: this.hasPendingConnection(SALES_PLATFORMS.BRAINTREE),
            error: this.errors.braintree,
            class: this.classes,
            'logo-alt': 'Braintree logo',
          },
        },
        {
          key: 'woocommerce',
          component: ConnectWoocommerce,
          display: false,
          events: {
            error: () =>
              this.updateTileToErrorStatus(SALES_PLATFORMS.WOOCOMMERCE),
            submit: (config) =>
              this.$refs.oauthConnect.launch(
                SALES_PLATFORMS.WOOCOMMERCE,
                config,
              ),
            modalAction: this.handleModalAction,
          },
          props: {
            ref: 'woocommerce',
            success: this.isFullyConnected(SALES_PLATFORMS.WOOCOMMERCE),
            disconnected: this.hasPendingConnection(
              SALES_PLATFORMS.WOOCOMMERCE,
            ),
            errors: this.errors,
            class: this.classes,
          },
        },
        {
          key: 'other',
          component: ConnectedAccountOnboardingModal,
          display: this.showPaymentCapacityConnections,
          events: {
            noPlatformModalAction: (action, platformName) =>
              this.trackNewPlatformModal(
                this.eventTrackingFields,
                action,
                INTEGRATION_TYPES.SALES,
                platformName,
              ),
          },
          props: {
            class: this.classes,
            isMissingPlatformTile: true,
          },
        },
      ];

      return this.sortTilesByStatus(tiles);
    },
    isMQL() {
      return mqlDefinition(
        this.newSignupParams.businessCountry,
        this.newSignupParams.businessMonthlyRevenue,
      );
    },
    shouldDisplayAllPlatforms() {
      return this.loadAllPlatforms || this.morePlatformsLoaded;
    },
    showPaymentCapacityConnections() {
      return !this.paymentsCapacityConnections || this.morePlatformsLoaded;
    },
    classes() {
      return {
        [this.$style.connector]: true,
        [this.$style['connector-mobile']]: this.shouldDisplayAllPlatforms,
      };
    },
    layoutClasses() {
      return {
        [this.$style.connector]: true,
      };
    },
    productLabel() {
      if (this.productSegmentLabel)
        return this.productSegmentLabel
          .replace(/_/g, ' ')
          .replace(/(?: |\b)(\w)/g, function (key, p1) {
            return key.toUpperCase();
          });
      return '';
    },
  },
  watch: {
    showNextStepsError(newVal) {
      this.$emit('update:showError', newVal);
    },
  },
  async mounted() {
    if (this.oauthPayload?.code || this.oauthPayload?.id) {
      await this.oauthSuccess(this.oauthPayload);
    } else if (this.oauthPayload?.error) {
      this.handleOauthError(this.oauthPayload);
    }
    this.$store.dispatch('SET_OAUTH_PAYLOAD', null);
    LocalStorage.removeItem(LocalStorageKeys.OAUTH_PROVIDER);
  },
  methods: {
    handleSeeMoreClick() {
      this.trackSeeMoreClick(this.eventTrackingFields);
      this.morePlatformsLoaded = !this.morePlatformsLoaded;
    },
    handleModalAction(platformName, action) {
      this.trackModal(
        platformName,
        action,
        this.eventTrackingFields,
        this.isMobile,
      );
    },
    handleRedirect(platformName) {
      this.trackRedirect(platformName, this.eventTrackingFields);
    },
    openModal(platformName) {
      this.handleTileClick(platformName, this.eventTrackingFields);
      const oauthPlatforms = [
        SALES_PLATFORMS.STRIPE,
        SALES_PLATFORMS.PAYPAL,
        SALES_PLATFORMS.SQUARE,
        SALES_PLATFORMS.AUTHORIZENET,
        SALES_PLATFORMS.BIGCOMMERCE,
        SALES_PLATFORMS.BRAINTREE,
      ];
      if (oauthPlatforms.includes(platformName)) {
        // See the following ticket as to why Stripe is the exception:
        // https://app.shortcut.com/clearbanc/story/179963/investigate-broken-stripe-connection-flow
        if (this.isMobile || platformName === SALES_PLATFORMS.STRIPE) {
          LocalStorage.setItem(LocalStorageKeys.OAUTH_PROVIDER, platformName);
          LocalStorage.setItem(
            LocalStorageKeys.OAUTH_REDIRECT_ROUTE,
            this.redirectRoute,
          );
          this.$refs.oauthConnect.launch(platformName, {
            noPopup: true,
            redirectRoute: this.redirectRoute,
          });
        } else {
          this.clearOauthParams();
          this.$refs.oauthConnect.launch(platformName);
        }
      } else {
        this.clearOauthParams();
        this.$refs[platformName].openModal();
      }
    },
    async oauthSuccess(payload) {
      if (payload.provider === SALES_PLATFORMS.SHOPIFY_FIVETRAN) {
        await this.$store.dispatchApiAction(
          'CONNECTED_ACCOUNT_CONFIGURE',
          payload,
        );
        this.$store.dispatchApiAction('FETCH_USER_EXTERNAL_ACCOUNTS');
        if (this.shopifyFivetranConfigureRequest.isError) {
          this.handleShopifyFivetranError(
            this.shopifyFivetranConfigureRequest.errorMessage,
          );
        }
      } else {
        // payload has provider, code, sometimes more depending on provider
        await this.$store.dispatch('oauthConnect', payload);
        if (this.oauthConnectionRequest.isError) {
          this.handleOauthError(payload);
          segmentErrorTracking(
            this.oauthConnectionRequest.errorMessage,
            this.$route.name,
          );
          return;
        }
      }

      this.trackAccountConnected(payload.provider);
    },
    trackAccountConnected(platformName) {
      let segmentEventName = SEGMENT_EVENTS.fe_account_connected;
      if (this.isMQL && this.externalAccounts.length === 1) {
        segmentEventName = SEGMENT_EVENTS.fe_mql_account_connected;
      }
      trackLinkedin(CONNECTION_STATUS.CONNECTED);
      analytics.track(segmentEventName, {
        category: 'Connect',
        label: this.productLabel,
        page: this.$route.name,
        accountType: this.platformType,
        ...this.eventTrackingFields,
      });

      analytics.track(SEGMENT_EVENTS.fe_sales_account_linked, {
        fb: true,
        platform: platformName,
        ...this.eventTrackingFields,
      });
    },
    async openPaymentPopup(payload) {
      await this.$refs.wooCommercePaymentConnector.open(payload);
    },
    sortTilesByStatus(tiles) {
      const disconnected = [];
      const warning = [];
      const processing = [];
      const connected = [];
      const neutral = [];
      tiles.forEach((tile) => {
        if (tile.props.disconnected) {
          disconnected.push(tile);
        } else if (tile.props.success) {
          connected.push(tile);
        } else if (tile.props?.warning) {
          warning.push(tile);
        } else if (tile.props?.processing) {
          processing.push(tile);
        } else {
          neutral.push(tile);
        }
      });

      return [
        ...disconnected,
        ...warning,
        ...processing,
        ...connected,
        ...neutral,
      ];
    },
    isSuccessOrDisconnected(platform) {
      return (
        this.getExtAccStatus(platform) === CONNECTION_STATUS.CONNECTED ||
        this.getExtAccStatus(platform) === CONNECTION_STATUS.DISCONNECTED ||
        this.getExtAccStatus(platform) === CONNECTION_STATUS.WARNING ||
        this.getExtAccStatus(platform) === CONNECTION_STATUS.PROCESSING
      );
    },
    handleShopifyFivetranError(errorMessage) {
      this.updateTileToErrorStatus(SALES_PLATFORMS.SHOPIFY_FIVETRAN);
      this.$emit('update:errorMessage', errorMessage);
    },
    // Clear items from localStorage from previous OAuth connections
    // this is to ensure that the OAuth flow is reset
    clearOauthParams() {
      LocalStorage.removeItem(LocalStorageKeys.OAUTH_PROVIDER);
      LocalStorage.removeItem(LocalStorageKeys.OAUTH_REDIRECT_ROUTE);
    },
  },
};
</script>

<style lang="less" module>
.container {
  margin: 20px auto 0;

  &.load-all-platforms {
    margin-top: 0;
  }
}
.connector {
  max-width: 800px;
  margin: auto;
  text-align: center;
}

.support-link {
  color: @color-red;
  text-decoration: underline;
}

button.list-toggle {
  display: block;
  margin: 30px auto;
}
</style>
